import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  InputNumber,
  Space,
  Tag,
  message,
  Card,
  Typography,
  Progress,
  Input,
  Tooltip,
  Grid,
  List,
  Divider,
  Checkbox,
  Popconfirm,
  DatePicker,
  Segmented
} from 'antd';
import { EditOutlined, ReloadOutlined, MedicineBoxOutlined, SearchOutlined, InfoCircleOutlined, EyeInvisibleOutlined, BarChartOutlined, DownloadOutlined, HistoryOutlined, FileExcelOutlined } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, BarChart, Bar, Legend, ReferenceLine, LabelList } from 'recharts';
import api from '../services/api';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import 'dayjs/locale/vi';

dayjs.extend(weekOfYear);

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const { RangePicker } = DatePicker;

// Helper to check permissions (consistent with App.tsx)
const hasPermission = (permission: string) => {
    const storedPermissions = localStorage.getItem('permissions');
    if (!storedPermissions || storedPermissions === 'undefined') return false;
    try {
        const permissionsList: string[] = JSON.parse(storedPermissions);
        return permissionsList.includes(permission);
    } catch (e) {
        return false;
    }
};

interface PharmacyStock {
  maview: string;
  ten_hang_hoa: string;
  ham_luong: string;
  hoat_chat: string;
  duong_dung: string;
  tong_ton_kho: number;
  sl_min: number;
  sl_max: number;
  ma_smile?: string;
  pr_number?: string;
  pr_qty?: number;
  pr_send_time?: string;
  pr_details_json?: string;
  phan_tram_ton: number;
  trang_thai: string;
  color: string;
  luong_xuat_30_ngay: number;
  thanh_tien_ton_kho: number;
  ma_loai_dv: number;
}


interface ExportStat {
  ngay: string;
  luongXuat: number;
}

const PharmacyManagement: React.FC = () => {
  const [data, setData] = useState<PharmacyStock[]>([]);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [showLowUsageOnly, setShowLowUsageOnly] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isOrderModalVisible, setIsOrderModalVisible] = useState(false);
  const [isStatsModalVisible, setIsStatsModalVisible] = useState(false);
  const [isLowUsageModalVisible, setIsLowUsageModalVisible] = useState(false);
  const [hideChemicals, setHideChemicals] = useState(true);
  const [editingItem, setEditingItem] = useState<PharmacyStock | null>(null);
  const [selectedItemForStats, setSelectedItemForStats] = useState<PharmacyStock | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [exportStats, setExportStats] = useState<ExportStat[]>([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsDateRange, setStatsDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([dayjs().subtract(30, 'day'), dayjs()]);
  const [statsViewType, setStatsViewType] = useState<'day' | 'week' | 'month' | 'year'>('day');
  const [chartType, setChartType] = useState<'line' | 'bar'>('bar');
  const [form] = Form.useForm();
  
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/Pharmacy/stock-status');
      console.log('[DEBUG] API Data Sample:', response.data?.[0]);
      if (Array.isArray(response.data)) {
        setData(response.data);
      } else {
        setData([]);
      }
      setSelectedRowKeys([]);
    } catch (error) {
      console.error('Error fetching pharmacy stock:', error);
      message.error('Không thể tải dữ liệu tồn kho');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchExportStats = useCallback(async (maview: string, range: [dayjs.Dayjs, dayjs.Dayjs]) => {
    setStatsLoading(true);
    try {
      const response = await api.get('/Pharmacy/export-stats', {
        params: {
          tuNgay: range[0].startOf('day').toISOString(),
          denNgay: range[1].endOf('day').toISOString(),
          maView: maview
        }
      });
      setExportStats(response.data || []);
    } catch (error) {
      console.error('Error fetching export stats:', error);
      message.error('Không thể tải thống kê xuất');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const groupData = useMemo(() => {
    if (!exportStats.length) return [];
    
    const grouped: { [key: string]: number } = {};
    
    exportStats.forEach(item => {
      const date = dayjs(item.ngay);
      let key = '';
      
      switch (statsViewType) {
        case 'week':
          key = `Tuần ${date.week()}, ${date.year()}`;
          break;
        case 'month':
          key = `Tháng ${date.month() + 1}/${date.year()}`;
          break;
        case 'year':
          key = `${date.year()}`;
          break;
        default:
          key = date.format('DD/MM/YYYY');
      }
      
      grouped[key] = (grouped[key] || 0) + item.luongXuat;
    });
    
    return Object.keys(grouped).map(key => ({
      name: key,
      value: grouped[key]
    }));
  }, [exportStats, statsViewType]);

  const averageValue = useMemo(() => {
    if (groupData.length === 0) return 0;
    const sum = groupData.reduce((acc, item) => acc + item.value, 0);
    return parseFloat((sum / groupData.length).toFixed(2));
  }, [groupData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleShowStats = (record: PharmacyStock) => {
    setSelectedItemForStats(record);
    setIsStatsModalVisible(true);
    fetchExportStats(record.maview, statsDateRange);
  };

  const handleEdit = useCallback((record: PharmacyStock) => {
    setEditingItem(record);
    form.setFieldsValue({
      sl_min: record.sl_min,
      sl_max: record.sl_max,
      ma_smile: record.ma_smile
    });
    setIsModalVisible(true);
  }, [form]);

  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();
      await api.post('/Pharmacy/threshold', {
        maview: editingItem?.maview,
        sl_min: values.sl_min,
        sl_max: values.sl_max,
        ma_smile: values.ma_smile
      });
      
      message.success('Cập nhật định mức thành công');
      setIsModalVisible(false);
      fetchData(); 
    } catch (error) {
      console.error('Error updating threshold:', error);
      message.error('Cập nhật định mức thất bại');
    }
  };

  const handleBulkHide = async () => {
    if (selectedRowKeys.length === 0) return;
    
    try {
      setLoading(true);
      await api.post('/Pharmacy/bulk-hide-drugs', {
        maviews: selectedRowKeys
      });
      message.success(`Đã ẩn ${selectedRowKeys.length} thuốc thành công`);
      setSelectedRowKeys([]);
      fetchData();
    } catch (error) {
      console.error('Error bulk hiding drugs:', error);
      message.error('Ẩn thuốc thất bại');
    } finally {
      setLoading(false);
    }
  };

  const handleHide = async (maview: string) => {
    try {
      await api.post('/Pharmacy/hide-drug', {
        maview: maview,
        isHidden: true
      });
      message.success('Đã ẩn thuốc khỏi danh sách');
      fetchData();
    } catch (error) {
      console.error('Error hiding drug:', error);
      message.error('Không thể ẩn thuốc');
    }
  };

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const lowerSearch = searchText.toLowerCase();
    return data.filter(item => {
      return (item.ten_hang_hoa?.toLowerCase() || '').includes(lowerSearch) ||
             (item.maview?.toLowerCase() || '').includes(lowerSearch) ||
             (item.hoat_chat?.toLowerCase() || '').includes(lowerSearch);
    });
  }, [data, searchText]);

  const lowUsageData = useMemo(() => {
    return data
      .filter(item => {
        const hasStock = (item.tong_ton_kho ?? 0) > 0;
        const isStagnant = (item.luong_xuat_30_ngay ?? 0) < ((item.tong_ton_kho ?? 0) * 0.05) || (item.luong_xuat_30_ngay ?? 0) < 2;
        
        if (hideChemicals && item.ma_loai_dv === 8) {
          return false;
        }
        
        return hasStock && isStagnant;
      })
      .sort((a, b) => (a.luong_xuat_30_ngay ?? 0) - (b.luong_xuat_30_ngay ?? 0) || (b.tong_ton_kho ?? 0) - (a.tong_ton_kho ?? 0));
  }, [data, hideChemicals]);

  const onSelectChange = useCallback((newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  }, []);

  const rowSelection = useMemo(() => ({
    selectedRowKeys,
    onChange: onSelectChange,
    columnWidth: 50, // Thiết lập độ rộng cố định cho cột chọn
    getCheckboxProps: (record: PharmacyStock) => ({
      disabled: !hasPermission('DuocMinMax'),
    }),
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
  }), [selectedRowKeys, onSelectChange]);

  const mobileColumns = useMemo(() => [
    {
      key: 'card',
      onCell: () => ({ style: { padding: '4px 0' } }),
      render: (_: any, item: PharmacyStock) => (
        <Card 
          size="small" 
          style={{ marginBottom: 4, borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
          title={<Text strong>{item.ten_hang_hoa}</Text>}
          extra={<Tag color={item.color} style={{color: '#fff', border: 'none', fontWeight: 'bold'}}>{item.trang_thai}</Tag>}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
              <Text type="secondary">Mã: {item.maview}</Text>
              {item.ma_smile && <Tag color="blue" style={{ margin: 0 }}>Smile: {item.ma_smile}</Tag>}
              <Text type="secondary">{item.duong_dung}</Text>
            </div>
            
            {item.pr_number && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                <Space>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Đơn hàng:</Text>
                  <Button 
                    type="link" 
                    size="small" 
                    style={{ padding: 0, height: 'auto' }}
                    onClick={() => {
                      setSelectedOrder(JSON.parse(item.pr_details_json || '{}'));
                      setIsOrderModalVisible(true);
                    }}
                  >
                    {item.pr_number}
                  </Button>
                  <Text type="secondary" style={{ fontSize: '12px' }}>- SL: <Text strong>{item.pr_qty}</Text></Text>
                </Space>
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {item.pr_send_time ? new Date(item.pr_send_time).toLocaleDateString('vi-VN') : ''}
                </Text>
              </div>
            )}

            <Text style={{ fontSize: '13px', marginTop: 4 }}>{item.hoat_chat} {item.ham_luong}</Text>
            
            <Divider style={{ margin: '8px 0' }} />
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space direction="vertical" size={0}>
                <Text strong style={{ fontSize: '18px' }}>{(item.tong_ton_kho ?? 0).toLocaleString()}</Text>
                <Text type="secondary" style={{ fontSize: '11px' }}>Tồn kho thực tế</Text>
              </Space>
              <Space direction="vertical" size={0} align="end">
                <Text style={{ fontSize: '12px' }}>Min: <span style={{color: '#faad14'}}>{item.sl_min}</span></Text>
                <Text style={{ fontSize: '12px' }}>Max: <span style={{color: '#1890ff'}}>{item.sl_max}</span></Text>
              </Space>
            </div>

            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>Mức độ đầy kho</Text>
                <Text strong style={{ fontSize: '12px' }}>{item.phan_tram_ton}%</Text>
              </div>
              <Progress 
                percent={item.phan_tram_ton > 100 ? 100 : (item.phan_tram_ton < 0 ? 0 : item.phan_tram_ton)} 
                strokeColor={item.color}
                size="small"
                showInfo={false}
                status={item.tong_ton_kho === 0 ? 'exception' : 'normal'}
              />
            </div>

            <Space style={{ marginTop: 8, width: '100%' }}>
              <Button 
                type="primary" 
                ghost 
                size="small"
                icon={<EditOutlined />} 
                style={{ flex: 1 }}
                onClick={() => handleEdit(item)}
                disabled={!hasPermission('DuocMinMax')}
              >
                Sửa
              </Button>
              <Button 
                type="default" 
                size="small"
                icon={<BarChartOutlined />}
                style={{ flex: 1 }}
                onClick={() => handleShowStats(item)}
              >
                Thống kê
              </Button>
              <Popconfirm
                title="Xác nhận ẩn thuốc?"
                onConfirm={() => handleHide(item.maview)}
                okText="Ẩn"
                cancelText="Hủy"
                disabled={!hasPermission('DuocMinMax')}
              >
                <Button 
                  danger 
                  ghost 
                  size="small"
                  icon={<EyeInvisibleOutlined />}
                  disabled={!hasPermission('DuocMinMax')}
                >
                  Ẩn
                </Button>
              </Popconfirm>
            </Space>
          </Space>
        </Card>
      ),
    }
  ], [handleEdit, handleHide]);

  const columns = useMemo(() => [
    {
      title: 'Mã View',
      dataIndex: 'maview',
      key: 'maview',
      width: 100,
    },
    {
      title: 'Mã Smile',
      dataIndex: 'ma_smile',
      key: 'ma_smile',
      width: 100,
      render: (val: string) => val ? <Tag color="blue">{val}</Tag> : <Text type="secondary">-</Text>
    },
    {
      title: 'Đơn hàng',
      key: 'latest_pr',
      width: 150,
      render: (_: any, record: PharmacyStock) => {
        const prList = record.pr_details_json ? JSON.parse(record.pr_details_json) : [];
        const isArray = Array.isArray(prList);
        const displayPr = isArray ? prList[0] : prList;
        
        return displayPr && displayPr.PRNumber ? (
          <Space direction="vertical" size={0}>
            <Space size={4}>
              <Button 
                type="link" 
                size="small" 
                style={{ padding: 0 }}
                onClick={() => {
                  setSelectedOrder(prList);
                  setIsOrderModalVisible(true);
                }}
              >
                {displayPr.PRNumber}
              </Button>
              {isArray && prList.length > 1 && (
                <Tag color="orange" style={{ fontSize: '10px', margin: 0, padding: '0 4px' }}>+{prList.length - 1}</Tag>
              )}
              <Text type="secondary" style={{ fontSize: '11px' }}>- SL: <Text strong>{displayPr.Qty}</Text></Text>
            </Space>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {displayPr.PRSendTime ? new Date(displayPr.PRSendTime).toLocaleString('vi-VN') : ''}
            </Text>
          </Space>
        ) : <Text type="secondary">-</Text>
      }
    },
    {
      title: 'Tên hàng hóa',
      dataIndex: 'ten_hang_hoa',
      key: 'ten_hang_hoa',
      width: 250,
      sorter: (a: PharmacyStock, b: PharmacyStock) => a.ten_hang_hoa.localeCompare(b.ten_hang_hoa),
      render: (text: string, record: PharmacyStock) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <div style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.45)' }}>
            {record.hoat_chat} {record.ham_luong}
          </div>
        </Space>
      ),
    },
    {
      title: 'Đường dùng',
      dataIndex: 'duong_dung',
      key: 'duong_dung',
      width: 120,
    },
    {
      title: 'Tồn kho',
      dataIndex: 'tong_ton_kho',
      key: 'tong_ton_kho',
      align: 'right' as const,
      width: 100,
      sorter: (a: PharmacyStock, b: PharmacyStock) => a.tong_ton_kho - b.tong_ton_kho,
      render: (val: number) => <Text strong>{(val ?? 0).toLocaleString()}</Text>,
    },
    {
      title: 'Ngưỡng',
      key: 'threshold',
      width: 120,
      render: (_: any, record: PharmacyStock) => (
        <Space direction="vertical" size={0}>
          <div style={{ fontSize: '12px' }}>
            Min: <span style={{color: '#faad14'}}>{record.sl_min}</span>
          </div>
          <div style={{ fontSize: '12px' }}>
            Max: <span style={{color: '#1890ff'}}>{record.sl_max}</span>
          </div>
        </Space>
      )
    },
    {
      title: 'Định mức tồn kho',
      key: 'status_progress',
      width: 250,
      sorter: (a: PharmacyStock, b: PharmacyStock) => a.phan_tram_ton - b.phan_tram_ton,
      render: (_: any, record: PharmacyStock) => (
        <Tooltip title={record.trang_thai}>
          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <Tag color={record.color} style={{ margin: 0, fontWeight: 'bold', border: 'none', color: '#fff' }}>
                {record.trang_thai}
              </Tag>
              {!record.trang_thai.includes('%') && (
                <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{record.phan_tram_ton}%</span>
              )}
            </div>
            <Progress 
              percent={record.phan_tram_ton > 100 ? 100 : (record.phan_tram_ton < 0 ? 0 : record.phan_tram_ton)} 
              strokeColor={record.color}
              showInfo={false}
              size="small"
              status={record.tong_ton_kho === 0 ? 'exception' : 'normal'}
            />
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 200, // Tăng thêm độ rộng để hiển thị đủ 2 nút
      fixed: 'right' as const,
      render: (_: any, record: PharmacyStock) => (
        <Space size="middle">
          {hasPermission('DuocMinMax') ? (
            <Button 
              type="primary" 
              ghost 
              size="small"
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
            >
              Sửa
            </Button>
          ) : (
            <Tooltip title="Bạn không có quyền chỉnh sửa định mức">
              <Button 
                type="primary" 
                ghost 
                size="small"
                icon={<EditOutlined />} 
                disabled
              >
                Sửa
              </Button>
            </Tooltip>
          )}
          <Button 
            type="default" 
            size="small"
            icon={<BarChartOutlined />}
            onClick={() => handleShowStats(record)}
          >
            Thống kê
          </Button>
        </Space>
      ),
    },
  ], [handleEdit, handleShowStats]);

  return (
    <Card bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        marginBottom: 16,
        gap: isMobile ? '12px' : '0'
      }}>
        <Title level={4} style={{ margin: 0 }}><MedicineBoxOutlined /> Quản lý tồn kho Dược</Title>
        <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }}>
          <Input 
            placeholder="Tìm thuốc..." 
            prefix={<SearchOutlined />} 
            style={{ width: isMobile ? '100%' : 300 }}
            onChange={e => setSearchText(e.target.value)}
            allowClear
          />
          <Button 
            type="default"
            icon={<HistoryOutlined />} 
            onClick={() => setIsLowUsageModalVisible(true)}
            style={{ width: isMobile ? '100%' : 'auto' }}
          >
            Thuốc ít dùng ({lowUsageData.length})
          </Button>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchData} 
            loading={loading}
            style={{ width: isMobile ? '100%' : 'auto' }}
          >
            Làm mới
          </Button>
          {hasPermission('DuocMinMax') && (
            <Popconfirm
              title={`Ẩn ${selectedRowKeys.length} mục đã chọn?`}
              description="Các thuốc này sẽ không hiển thị trong danh sách nữa."
              onConfirm={handleBulkHide}
              okText="Ẩn"
              cancelText="Hủy"
              disabled={selectedRowKeys.length === 0}
            >
              <Button 
                danger 
                icon={<EyeInvisibleOutlined />} 
                disabled={selectedRowKeys.length === 0}
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                Ẩn mục đã chọn
              </Button>
            </Popconfirm>
          )}
        </Space>
      </div>
      
      {isMobile ? (
        <Table 
          columns={mobileColumns} 
          dataSource={filteredData} 
          rowKey="maview" 
          loading={loading}
          pagination={false}
          rowSelection={rowSelection}
          scroll={{ y: 'calc(100vh - 250px)' }}
          virtual
          showHeader={false}
          size="small"
        />
      ) : (
        <Table 
          columns={columns} 
          dataSource={filteredData} 
          rowKey="maview" 
          loading={loading}
          pagination={false}
          rowSelection={rowSelection}
          scroll={{ y: 'calc(100vh - 280px)', x: 1400 }} // Tăng x lên 1400 để khớp với tổng độ rộng các cột
          virtual
          size="middle"
          bordered
        />
      )}

      <Modal
        title={
          <Space>
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
            <span>Định mức: {editingItem?.ten_hang_hoa}</span>
          </Space>
        }
        open={isModalVisible}
        onOk={handleUpdate}
        onCancel={() => setIsModalVisible(false)}
        okText="Lưu thay đổi"
        cancelText="Hủy"
        destroyOnClose
        width={isMobile ? '95%' : 500}
      >
        <Form form={form} layout="vertical">
          <Form.Item 
            name="ma_smile" 
            label="Mã Smile (ItemCode)" 
            extra="Mã hàng hóa tương ứng trên hệ thống Smile."
          >
            <Input placeholder="Nhập mã ItemCode..." />
          </Form.Item>
          <Form.Item 
            name="sl_min" 
            label="Số lượng Tối thiểu (MIN)" 
            extra="Ngưỡng báo động cần nhập hàng ngay."
            rules={[{ required: true, message: 'Vui lòng nhập SL MIN' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} placeholder="Nhập số lượng..." />
          </Form.Item>
          <Form.Item 
            name="sl_max" 
            label="Số lượng Tối đa (MAX)" 
            extra="Mức tồn kho lý tưởng tối đa."
            rules={[{ required: true, message: 'Vui lòng nhập SL MAX' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} placeholder="Nhập số lượng..." />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <Space>
            <MedicineBoxOutlined style={{ color: '#52c41a' }} />
            <span>Chi tiết đơn hàng gần đây</span>
          </Space>
        }
        open={isOrderModalVisible}
        onCancel={() => setIsOrderModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsOrderModalVisible(false)}>
            Đóng
          </Button>
        ]}
        width={600}
      >
        {selectedOrder && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {(Array.isArray(selectedOrder) ? selectedOrder : [selectedOrder]).map((order: any, index: number) => (
              <div key={order.PRNumber || index} style={{ marginBottom: index === 0 && Array.isArray(selectedOrder) && selectedOrder.length > 1 ? 24 : 0 }}>
                {Array.isArray(selectedOrder) && selectedOrder.length > 1 && (
                  <Divider orientation="left" style={{ margin: '0 0 12px 0' }}>
                    <Tag color="blue">Đơn hàng {index + 1}</Tag>
                  </Divider>
                )}
                <List
                  size="small"
                  bordered
                  dataSource={[
                    { label: 'Số PR', value: order.PRNumber },
                    { label: 'Số lượng', value: <Text strong color="red">{order.Qty}</Text> },
                    { label: 'Ngày PR', value: order.PRDate ? new Date(order.PRDate).toLocaleDateString('vi-VN') : '-' },
                    { label: 'Ngày hẹn (Due)', value: order.DueDate ? new Date(order.DueDate).toLocaleDateString('vi-VN') : '-' },
                    { label: 'Bộ phận', value: order.Department },
                    { label: 'Cấp duyệt', value: Number(order.PRNumber) > 900000 ? <Tag color="purple">Tập đoàn</Tag> : <Tag color="blue">Bệnh viện</Tag> },
                    { label: 'Người đã ký', value: order.SignedBy ? <Text strong style={{ color: '#1890ff' }}>{order.SignedBy}</Text> : <Text type="secondary">Chưa có người ký</Text> },
                    { label: 'Người đặt', value: order.Orderby },
                    { label: 'Tham chiếu (Reference)', value: order.Reference },
                    { label: 'Mô tả', value: order.MyDescription },
                    { 
                      label: 'Trạng thái', 
                      value: (() => {
                        switch (order.StatePR) {
                          case 1: return <Tag color="default">Đã tạo</Tag>;
                          case 2: return <Tag color="processing">Đang duyệt</Tag>;
                          case 3: return <Tag color="warning">Đang tạo PO</Tag>;
                          case 9: return <Tag color="success">Đã xong PO</Tag>;
                          default: return order.StatePR;
                        }
                      })()
                    },
                    { label: 'Giờ tạo PR', value: order.CreatedTime ? new Date(order.CreatedTime).toLocaleString('vi-VN') : '-' },
                    { label: 'Giờ gửi gần nhất', value: order.PRSendTime ? new Date(order.PRSendTime).toLocaleString('vi-VN') : '-' },
                  ]}
                  renderItem={item => (
                    <List.Item>
                      <Text strong>{item.label}:</Text> <Text>{item.value || '-'}</Text>
                    </List.Item>
                  )}
                />
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 32 }}>
            <Space>
              <BarChartOutlined style={{ color: '#1890ff' }} />
              <span>Thống kê xuất: {selectedItemForStats?.ten_hang_hoa}</span>
            </Space>
          </div>
        }
        open={isStatsModalVisible}
        onCancel={() => setIsStatsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsStatsModalVisible(false)}>
            Đóng
          </Button>
        ]}
        width={isMobile ? '98%' : 900}
        style={{ top: 20 }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Card size="small" style={{ background: '#fafafa' }}>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '16px', 
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Space wrap>
                <RangePicker 
                  value={statsDateRange} 
                  onChange={(val) => {
                    if (val && val[0] && val[1]) {
                      const range: [dayjs.Dayjs, dayjs.Dayjs] = [val[0], val[1]];
                      setStatsDateRange(range);
                    }
                  }}
                  format="DD/MM/YYYY"
                />
                <Button 
                  type="primary" 
                  icon={<ReloadOutlined />} 
                  onClick={() => selectedItemForStats && fetchExportStats(selectedItemForStats.maview, statsDateRange)}
                  loading={statsLoading}
                >
                  Lấy dữ liệu
                </Button>
              </Space>
              
              <Space wrap>
                <Segmented
                  options={[
                    { label: 'Ngày', value: 'day' },
                    { label: 'Tuần', value: 'week' },
                    { label: 'Tháng', value: 'month' },
                    { label: 'Năm', value: 'year' },
                  ]}
                  value={statsViewType}
                  onChange={(val: any) => setStatsViewType(val)}
                />
                <Segmented
                  options={[
                    { label: 'Cột', value: 'bar' },
                    { label: 'Đường', value: 'line' },
                  ]}
                  value={chartType}
                  onChange={(val: any) => setChartType(val)}
                />
              </Space>
            </div>
          </Card>

          <div style={{ height: isMobile ? 300 : 450, width: '100%', marginTop: 16 }}>
            {statsLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Progress type="circle" percent={100} status="active" strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }} />
              </div>
            ) : groupData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'bar' ? (
                  <BarChart data={groupData} margin={{ top: 10, right: 60, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fontSize: 12 }} 
                      interval={isMobile ? 'preserveStartEnd' : 0}
                      angle={groupData.length > 10 ? -45 : 0}
                      textAnchor={groupData.length > 10 ? 'end' : 'middle'}
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <ChartTooltip 
                      contentStyle={{ borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: 'none' }}
                    />
                    <Legend verticalAlign="top" height={36}/>
                    <Bar 
                      name="Số lượng xuất" 
                      dataKey="value" 
                      fill="#1890ff" 
                      radius={[4, 4, 0, 0]} 
                      barSize={isMobile ? 20 : 40}
                    >
                      <LabelList 
                        dataKey="value" 
                        position="top" 
                        formatter={(val: any) => (val && val > 0) ? val.toLocaleString() : ''}
                        style={{ fontSize: 11, fill: '#666', fontWeight: '500' }}
                      />
                    </Bar>
                    {groupData.length > 0 && (
                      <ReferenceLine 
                        y={averageValue} 
                        stroke="#ff4d4f" 
                        strokeDasharray="3 3"
                        label={{ 
                          position: 'right', 
                          value: `TB: ${averageValue.toLocaleString()}`, 
                          fill: '#ff4d4f', 
                          fontSize: 12,
                          fontWeight: 'bold',
                          offset: 10
                        }} 
                      />
                    )}
                  </BarChart>
                ) : (
                  <LineChart data={groupData} margin={{ top: 10, right: 60, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fontSize: 12 }}
                      interval={isMobile ? 'preserveStartEnd' : 0}
                      angle={groupData.length > 10 ? -45 : 0}
                      textAnchor={groupData.length > 10 ? 'end' : 'middle'}
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <ChartTooltip 
                      contentStyle={{ borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: 'none' }}
                    />
                    <Legend verticalAlign="top" height={36}/>
                    <Line 
                      name="Số lượng xuất" 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#1890ff" 
                      strokeWidth={3} 
                      dot={{ r: 4, fill: '#1890ff', strokeWidth: 2, stroke: '#fff' }}
                      activeDot={{ r: 6 }}
                    >
                      <LabelList 
                        dataKey="value" 
                        position="top" 
                        offset={10}
                        formatter={(val: any) => (val && val > 0) ? val.toLocaleString() : ''}
                        style={{ fontSize: 11, fill: '#666', fontWeight: '500' }}
                      />
                    </Line>
                    {groupData.length > 0 && (
                      <ReferenceLine 
                        y={averageValue} 
                        stroke="#ff4d4f" 
                        strokeDasharray="3 3"
                        label={{ 
                          position: 'right', 
                          value: `TB: ${averageValue.toLocaleString()}`, 
                          fill: '#ff4d4f', 
                          fontSize: 12,
                          fontWeight: 'bold',
                          offset: 10
                        }} 
                      />
                    )}
                  </LineChart>
                )}
              </ResponsiveContainer>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'rgba(0,0,0,0.45)' }}>
                <BarChartOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.2 }} />
                <Text type="secondary">Không có dữ liệu trong khoảng thời gian này</Text>
              </div>
            )}
          </div>
          
          {groupData.length > 0 && (
            <div style={{ textAlign: 'right', marginTop: 8 }}>
              <Space direction="vertical" align="end" size={0}>
                <Text strong>Tổng cộng: {groupData.reduce((sum, item) => sum + item.value, 0).toLocaleString()}</Text>
                {averageValue > 0 && selectedItemForStats && (
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    Dự kiến còn dùng được trong khoảng:{' '}
                    <Text strong style={{ color: '#faad14' }}>
                      {Math.round(selectedItemForStats.tong_ton_kho / averageValue * 10) / 10}
                    </Text>
                    {' '}
                    {(() => {
                      switch (statsViewType) {
                        case 'week': return 'Tuần';
                        case 'month': return 'Tháng';
                        case 'year': return 'Năm';
                        default: return 'Ngày';
                      }
                    })()}
                  </Text>
                )}
              </Space>
            </div>
          )}
        </Space>
      </Modal>

      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingRight: 40 }}>
            <Space>
              <HistoryOutlined style={{ color: '#ff4d4f' }} />
              <span>Danh sách thuốc ít dùng (Tồn cao, xuất ít trong 30 ngày)</span>
            </Space>
            <Checkbox 
              checked={hideChemicals} 
              onChange={e => setHideChemicals(e.target.checked)}
            >
              Ẩn hóa chất (Loại 8)
            </Checkbox>
          </div>
        }
        open={isLowUsageModalVisible}
        onCancel={() => setIsLowUsageModalVisible(false)}
        afterOpenChange={(open) => {
          if (open) setHideChemicals(true);
        }}
        footer={[
          <Button key="close" onClick={() => setIsLowUsageModalVisible(false)}>
            Đóng
          </Button>
        ]}
        width={1000}
      >
        <Table
          size="small"
          dataSource={lowUsageData}
          rowKey="maview"
          pagination={false}
          scroll={{ y: 500, x: 900 }}
          virtual
          bordered
          columns={[
            { title: 'Mã View', dataIndex: 'maview', key: 'maview', width: 100 },
            { title: 'Tên hàng hóa', dataIndex: 'ten_hang_hoa', key: 'ten_hang_hoa' },
            { 
              title: 'Tồn kho', 
              dataIndex: 'tong_ton_kho', 
              key: 'tong_ton_kho', 
              align: 'right',
              render: (val) => <Text strong>{(val ?? 0).toLocaleString()}</Text>
            },
            { 
              title: 'Xuất (30n)', 
              dataIndex: 'luong_xuat_30_ngay', 
              key: 'luong_xuat_30_ngay', 
              align: 'right',
              render: (val) => <Text type="danger" strong>{(val ?? 0).toLocaleString()}</Text>
            },
            { 
              title: 'Thành tiền tồn', 
              dataIndex: 'thanh_tien_ton_kho', 
              key: 'thanh_tien_ton_kho', 
              align: 'right',
              sorter: (a, b) => (a.thanh_tien_ton_kho ?? 0) - (b.thanh_tien_ton_kho ?? 0),
              render: (val) => <Text strong>{(val ?? 0).toLocaleString()}đ</Text>
            },
            {
              title: 'Tỷ lệ xuất/tồn',
              key: 'ratio',
              align: 'right',
              render: (_, record) => {
                const ratio = record.tong_ton_kho > 0 ? (record.luong_xuat_30_ngay / record.tong_ton_kho * 100).toFixed(1) : 0;
                return <Tag color="orange">{ratio}%</Tag>
              }
            }
          ]}
        />
      </Modal>
    </Card>
  );
};

export default PharmacyManagement;


